import React from "react";

import _ from "lodash";

import { formatNumericCurrency } from "utils";

import { CurrencyCode } from "consts";

import { Incentive, Practice } from "models";

import { Flex, Table, Tooltip } from "components";

interface PracticesTableProps {
  practices: Practice[];
  currency: CurrencyCode;
}

const yesNo = (value: boolean) => (value ? "Yes" : "No");

// Conditions beyond the amount, in plain words
const incentiveConditions = (incentive: Incentive) =>
  _.compact([
    incentive.requirementLevel === "requiredForProgram" && "Data required for the program",
    incentive.tillageDepthLimit !== undefined && `Tillage at most ${incentive.tillageDepthLimit} cm deep`,
    incentive.ploughingAreaLimit !== undefined && `At most ${incentive.ploughingAreaLimit} % of the crop area ploughed`,
    incentive.speciesCountThreshold !== undefined && `At least ${incentive.speciesCountThreshold} species`,
    incentive.exclusiveWith?.length && `Only the highest paying of: ${incentive.exclusiveWith.map(_.startCase).join(", ")}`,
    incentive.elementIds?.length && `Applies to ${incentive.elementIds.length} specific crops / practices`,
  ]);

const PracticesTable: React.FC<PracticesTableProps> = ({ practices, currency }) => (
  <Table<Practice>
    data={practices}
    rowKey="slug"
    pagination={false}
    columns={[
      { title: "Practice", dataIndex: "slug", render: _.startCase },
      { title: "Required", dataIndex: "required", render: yesNo },
      { title: "Depends on", dataIndex: "dependencies", render: (dependencies: string[]) => dependencies.map(_.startCase).join(", ") || "None" },
      { title: "Baseline years", dataIndex: "baselineYears" },
      { title: "Farmer can enrol", dataIndex: "enrollable", render: yesNo },
      {
        title: "Incentives",
        dataIndex: "incentives",
        render: (incentives: Incentive[]) =>
          incentives.length ? (
            <Flex vertical gap={4}>
              {incentives.map((incentive) => (
                <Tooltip key={incentive.name} variant="neutral" title={incentiveConditions(incentive).join(" · ") || undefined}>
                  <span>{`${_.startCase(incentive.name)}: ${formatNumericCurrency(incentive.amount, { currency })} / ha`}</span>
                </Tooltip>
              ))}
            </Flex>
          ) : (
            "None"
          ),
      },
    ]}
  />
);

export default PracticesTable;
